// src/routes/index.jsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import SuperAdminOverview from '../pages/SuperAdminOverview';
import SuperAdminEnterprises from '../pages/SuperAdminEnterprises';
import SuperAdminAnalytics from '../pages/SuperAdminAnalytics';
import SuperAdminSupport from '../pages/SuperAdminSupport';
import SuperAdminEnterpriseWorkspace from '../pages/SuperAdminEnterpriseWorkspace';
import SuperAdminOperationsHub from '../pages/SuperAdminOperationsHub';
import EnterpriseDashboard from '../pages/EnterpriseDashboard';
import ManageAssets from '../pages/ManageAssets';
import MonitorAssets from '../pages/MonitorAssets';
import CloudConnectionsPage from '../pages/CloudConnectionsPage';
import CloudMonitorAWS from '../pages/CloudMonitorAWS';
import CloudMonitorAzure from '../pages/CloudMonitorAzure';
import CloudMonitorM365 from '../pages/CloudMonitorM365';
import CmdbPage from '../pages/CmdbPage';
import EnterpriseProfile from '../pages/EnterpriseProfile';
import Pricing from '../pages/Pricing';
import BillingRevenue from '../pages/BillingRevenue';
import ManagePartners from '../pages/ManagePartners';
import PartnerDashboard from '../pages/PartnerDashboard';
import AssetPublicDetailsPage from '../pages/AssetPublicDetailsPage';
import DemoExplorerPage from '../pages/DemoExplorerPage';
import OAuthCallbackPage from '../pages/OAuthCallbackPage';
import ReportsPage from '../pages/ReportsPage';
import NetworkScanPage from '../pages/NetworkScanPage';
import ConnectorVaultPage from '../pages/ConnectorVaultPage';
import MaintenancePage from '../pages/MaintenancePage';
import MarketingPage from '../pages/MarketingPage';

function AuthLoginRedirect() {
  const location = useLocation();

  React.useEffect(() => {
    const target = `${getDefaultAuthBase()}/login${location.search || ''}`;
    window.location.replace(target);
  }, [location.search]);

  return <div>Redirecting to sign in...</div>;
}

function AuthPathRedirect({ path }) {
  const location = useLocation();

  React.useEffect(() => {
    window.location.replace(`${getDefaultAuthBase()}${path}${location.search || ''}`);
  }, [location.search, path]);

  return <div>Redirecting to Susha Auth...</div>;
}

function getDefaultAuthBase() {
  const envBase = process.env.REACT_APP_AUTH_URL ? process.env.REACT_APP_AUTH_URL.replace(/\/+$/, '') : '';
  if (envBase) return envBase;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      return 'http://localhost:3000';
    }
  }
  return 'https://auth.sushaservices.com';
}

function AuthRoleManagementRedirect() {
  const location = useLocation();

  React.useEffect(() => {
    const target = `${getDefaultAuthBase()}/user-role-management${location.search || ''}`;
    window.location.replace(target);
  }, [location.search]);

  return <div>Redirecting to user and role management...</div>;
}

export const ROUTES = {
  CLOUD_PLATFORM: '/cloud-platform',
  LOGIN: '/login',
  REGISTER: '/sign-up',
  SUPERADMIN: '/superadmin',
  SUPERADMIN_ENTERPRISES: '/superadmin/enterprises',
  SUPERADMIN_ENTERPRISE: '/superadmin/enterprises/:id',
  SUPERADMIN_PARTNERS: '/superadmin/partners',
  SUPERADMIN_ANALYTICS: '/superadmin/analytics',
  SUPERADMIN_SUPPORT: '/superadmin/support',
  SUPERADMIN_BILLING: '/superadmin/billing',
  SUPERADMIN_OPERATIONS: '/superadmin/operations',
  ENTERPRISE: '/enterprise',
  DASHBOARD: '/enterprise',
  PARTNER: '/partner',
  ENTERPRISE_PROFILE: '/enterprise/profile',
  ASSETS: '/enterprise/assets',
  MONITOR: '/enterprise/monitor',
  CLOUD: '/enterprise/cloud',
  CMDB: '/enterprise/cmdb',
  REPORTS: '/enterprise/reports',
  NETWORK_SCAN: '/enterprise/network-scan',
  CONNECTORS: '/enterprise/connectors',
  MAINTENANCE: '/enterprise/maintenance',
  ROLE_MANAGEMENT: '/enterprise/role-management',
  CLOUD_M365: '/enterprise/cloud/m365',
  CLOUD_AZURE: '/enterprise/cloud/azure',
  CLOUD_AWS: '/enterprise/cloud/aws',
  RESET_PASSWORD: '/reset-password',
  CHANGE_PASSWORD: '/change-password',
  VERIFY_COMPANY: '/verify-company',
  NOT_VERIFIED: '/not-verified',
  PRICING: '/pricing',
  DEMO_EXPLORER: '/demo-explorer',
  ASSET_PUBLIC_DETAILS: '/asset-details/:assetId',
  OAUTH_CALLBACK: '/oauth/callback',
};

export const appRoutes = [
  { path: '/', element: <AuthLoginRedirect /> },
  { path: ROUTES.CLOUD_PLATFORM, element: <MarketingPage /> },
  { path: ROUTES.LOGIN, element: <AuthLoginRedirect /> },
  { path: ROUTES.REGISTER, element: <AuthPathRedirect path="/sign-up" /> },
  { path: '/register', element: <Navigate to={ROUTES.REGISTER} replace /> },
  { path: ROUTES.VERIFY_COMPANY, element: <AuthPathRedirect path="/verify-email" /> },
  { path: ROUTES.NOT_VERIFIED, element: <AuthLoginRedirect /> },
  { path: ROUTES.PRICING, element: <Pricing /> },
  { path: ROUTES.DEMO_EXPLORER, element: <DemoExplorerPage /> },
  { path: ROUTES.ASSET_PUBLIC_DETAILS, element: <AssetPublicDetailsPage /> },
  { path: ROUTES.OAUTH_CALLBACK, element: <OAuthCallbackPage /> },
  {
    path: ROUTES.SUPERADMIN,
    element: <SuperAdminOverview />,
    meta: { auth: true, role: 'super' },
  },
  {
    path: ROUTES.SUPERADMIN_PARTNERS,
    element: <ManagePartners />,
    meta: { auth: true, role: 'super' },
  },
  {
    path: ROUTES.SUPERADMIN_ENTERPRISES,
    element: <SuperAdminEnterprises />,
    meta: { auth: true, role: 'super' },
  },
  { path: ROUTES.SUPERADMIN_ENTERPRISE, element: <SuperAdminEnterpriseWorkspace />, meta: { auth: true, role: 'super' } },
  { path: ROUTES.SUPERADMIN_ANALYTICS, element: <SuperAdminAnalytics />, meta: { auth: true, role: 'super' } },
  { path: ROUTES.SUPERADMIN_SUPPORT, element: <SuperAdminSupport />, meta: { auth: true, role: 'super' } },
  { path: ROUTES.SUPERADMIN_OPERATIONS, element: <SuperAdminOperationsHub />, meta: { auth: true, role: 'super' } },
  {
    path: ROUTES.SUPERADMIN_BILLING,
    element: <BillingRevenue />,
    meta: { auth: true, role: 'super' },
  },
  {
    path: ROUTES.ENTERPRISE,
    element: <EnterpriseDashboard />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'dashboard' },
  },
  {
    path: ROUTES.PARTNER,
    element: <PartnerDashboard />,
    meta: { auth: true, role: 'partner' },
  },
  {
    path: ROUTES.ENTERPRISE_PROFILE,
    element: <EnterpriseProfile />,
    meta: { auth: true, role: 'enterprise_admin' },
  },
  {
    path: ROUTES.CLOUD,
    element: <CloudConnectionsPage />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'cloud' },
  },
  {
    path: ROUTES.CMDB,
    element: <CmdbPage />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'cmdb' },
  },
  {
    path: ROUTES.REPORTS,
    element: <ReportsPage />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'reports' },
  },
  {
    path: ROUTES.NETWORK_SCAN,
    element: <NetworkScanPage />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'network_scan' },
  },
  {
    path: ROUTES.CONNECTORS,
    element: <ConnectorVaultPage />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'connectors' },
  },
  {
    path: ROUTES.MAINTENANCE,
    element: <MaintenancePage />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'maintenance' },
  },
  {
    path: ROUTES.ROLE_MANAGEMENT,
    element: <AuthRoleManagementRedirect />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'role_management' },
  },
  {
    path: ROUTES.CLOUD_AWS,
    element: <CloudMonitorAWS />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'cloud' },
  },
  {
    path: ROUTES.CLOUD_AZURE,
    element: <CloudMonitorAzure />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'cloud' },
  },
  {
    path: ROUTES.CLOUD_M365,
    element: <CloudMonitorM365 />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'cloud' },
  },
  {
    path: ROUTES.ASSETS,
    element: <ManageAssets />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'assets' },
  },
  {
    path: ROUTES.MONITOR,
    element: <MonitorAssets />,
    meta: { auth: true, role: 'enterprise_admin', pageKey: 'monitor' },
  },
  { path: ROUTES.RESET_PASSWORD, element: <AuthPathRedirect path="/reset-password" /> },
  { path: ROUTES.CHANGE_PASSWORD, element: <AuthPathRedirect path="/change-password" /> },
  { path: '*', element: <div>Not Found</div> },
];
